// src/components/dashboards/TabularWizard.tsx
// ---------------------------------------------------------------------------
// Phase 2B — CSV/Excel 6-Step Wizard
//
// Steps:
//   1. Upload: select .csv/.xlsx file, parse server-side
//   2. Mode:   CREATE (new patients) or IMPORT (into existing)
//   3. Grouping: auto-detect + user override
//   4. Schema:  LLM column descriptions + user editing
//   5. Patient Resolution (CREATE: preview; IMPORT: matching + disambiguation)
//   6. Preview + cost estimate → Start processing
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  PlayArrow as StartIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  TableChart as TableIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import api from "../../api/api";
import { useTranslation } from "../../i18n";
import { useSnackbar } from "notistack";
import { athenaColors } from "../layout/AthenaDialogStyles";
import logger from "utils/logger";

// ── Types ────────────────────────────────────────────────────────────────────

interface ParseResult {
  upload_id: string;
  file_name: string;
  row_count: number;
  column_count: number;
  columns: string[];
  sample_rows: Record<string, unknown>[];
  sheets: string[];
  warnings: string[];
  grouping_suggestion: { column: string; reason: string } | null;
  header_row: number;
  raw_preview: (string | null)[][];
  format_issues: string[];
  exceeds_row_limit?: boolean;
  exceeds_col_limit?: boolean;
}

interface SchemaResult {
  column_descriptions: Record<string, string>;
  suggested_grouping: { column: string; reason: string } | null;
  suggested_patient_name_column: string | null;
  /** LLM-suggested column most likely to hold the patient's hospital ID
   *  (NHC, MRN, study ID). Optional in payload; null means no obvious column. */
  suggested_external_id_column?: string | null;
  token_usage?: { input_tokens: number; output_tokens: number };
}

interface TemplateMatch {
  id: string;
  name: string;
  overlap_percent: number;
  column_definitions: Record<string, string>;
  grouping_config: { enabled: boolean; column: string } | null;
  use_count: number;
}

interface PatientMatchResult {
  identifier: string;
  match_count: number;
  matches: {
    id: number;
    name: string;
    external_id: string;
    domains: string[];
    document_count: number;
    created_at: string | null;
  }[];
  status: "matched" | "ambiguous" | "not_found";
}

/** A single staged patient row returned by the wizard's review_only flow.
 *  Materialized as a PatientCardData in the V2 cards UI. */
export interface StagedTabularPatient {
  rowIndex: number;
  name: string;
  /** Per-row external id (NHC / MRN / study id) when the user mapped an
   *  external-id column; "" otherwise. Pre-fills the card's external_id field. */
  externalId: string;
  /** Pre-built synthetic Document text (header + per-row JSON). Sent verbatim
   *  to the backend on the V2 form's submit, becomes a `Document(text=...)`
   *  attached to the patient. */
  tabularPromptText: string;
}

interface TabularWizardProps {
  environmentId: string;
  /** All ticked environments (cursor_logs/043). environmentId is the primary
   *  (index 0); NEW patients are created in every env in this list. */
  environmentIds?: string[];
  /** Render for a DARK host (the V2 Athena modal, cursor_logs/049). Turns the
   *  inner card surfaces into dark glass, forces text/inputs/buttons to the
   *  Athena dark-dialog palette, and makes the sticky footer opaque. Off by
   *  default so the light V1 standalone mount is unaffected. */
  dark?: boolean;
  /** Spreadsheet to parse on mount (when launched from a drop on the V2
   *  form) so the user doesn't have to re-select it. Joan, 2026-05-27. */
  initialFile?: File | null;
  onComplete?: () => void;
  /** Optional V2 cards-UI integration. When supplied, the wizard's final
   *  step renders a secondary "Review & edit in cards" button alongside
   *  "Confirm & Start". On click, the wizard POSTs with `review_only=true`,
   *  receives the staged per-patient data, and invokes this callback. The
   *  V2 modal then closes and the parent form materializes the cards.
   *  V1's mount omits this prop — only "Confirm & Start" renders. */
  onReviewInCards?: (staged: {
    uploadId: string;
    patients: StagedTabularPatient[];
  }) => void;
}

type WizardMode = "create" | "import";
type MergeMode = "append" | "upsert" | "replace";

// ── Constants ────────────────────────────────────────────────────────────────

const getStepLabels = (t: (key: string, fallback: string) => string) => [
  t("csv.step_upload", "Upload"),
  t("csv.step_mode", "Mode"),
  t("csv.step_grouping", "Grouping"),
  t("csv.step_column_definitions", "Column Definitions"),
  t("csv.step_patient_resolution", "Patient Resolution"),
  t("csv.step_confirm", "Confirm & Start"),
];

const CSV_ASCII = `  patient_data.xlsx              What we detect
  ─────────────────              ──────────────

  ┌─────────┬──────┬───────┐     Detected: 3 patients
  │ name    │ age  │ stage │     Grouping: one row = one patient
  ├─────────┼──────┼───────┤     ┌──────────────────────┐
  │ Alice   │  54  │  IIA  │     │ name  → Patient Name │
  │ Bob     │  67  │  IIIB │     │ age   → Age at Dx    │
  │ Carol   │  41  │  IA   │     │ stage → TNM Stage    │
  └─────────┴──────┴───────┘     └──────────────────────┘`;

// ASCII examples for format guidance
const FORMAT_GOOD = `  ┌────────┬─────┬───────┬────────┐
  │ Name   │ Age │ Stage │ Site   │  ← Single header row
  ├────────┼─────┼───────┼────────┤
  │ Alice  │  54 │  IIA  │ Skin   │  ← Data starts here
  │ Bob    │  67 │  IIIB │ Lung   │
  └────────┴─────┴───────┴────────┘`;

const FORMAT_BAD_MERGED = `  ┌────────────────┬───────────────┐
  │   Demographics │   Clinical    │  ← Merged category cells
  ├────────┬───────┼───────┬───────┤
  │ Name   │  Age  │ Stage │ Site  │  ← Actual headers
  ├────────┼───────┼───────┼───────┤
  │ Alice  │   54  │  IIA  │ Skin  │
  └────────┴───────┴───────┴───────┘`;

const FORMAT_BAD_MULTI = `  ┌────────┬───────┬───────┬───────┐
  │ Study  │       │       │       │  ← Category row (half empty)
  ├────────┼───────┼───────┼───────┤
  │ Name   │  Age  │ Stage │ Site  │  ← Real headers on row 2
  ├────────┼───────┼───────┼───────┤
  │ Alice  │   54  │  IIA  │ Skin  │
  └────────┴───────┴───────┴───────┘`;

// ── Component ────────────────────────────────────────────────────────────────

const TabularWizard: React.FC<TabularWizardProps> = ({
  environmentId,
  environmentIds,
  dark = false,
  initialFile,
  onComplete,
  onReviewInCards,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Step state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Step 1: Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [headerRow, setHeaderRow] = useState(0);
  const [lastUploadedFile, setLastUploadedFile] = useState<File | null>(null);

  // Step 2: Mode
  const [mode, setMode] = useState<WizardMode>("create");

  // Step 3: Grouping
  const [groupingEnabled, setGroupingEnabled] = useState(false);
  const [groupingColumn, setGroupingColumn] = useState("");

  // Step 4: Schema inference
  const [columnDescriptions, setColumnDescriptions] = useState<Record<string, string>>({});
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [patientNameColumn, setPatientNameColumn] = useState("");
  // Optional column carrying each row's external_id (NHC / MRN / study ID).
  // When set, the backend uses merge_strict to attach data to an existing
  // patient with the same external_id (silently if names agree, refusing
  // the row when they conflict). See cursor_logs/036 §15.
  const [externalIdColumn, setExternalIdColumn] = useState<string>("");
  const [suggestedTemplates, setSuggestedTemplates] = useState<TemplateMatch[]>([]);
  const [schemaInferred, setSchemaInferred] = useState(false);

  // Step 5: Patient resolution
  const [matchResults, setMatchResults] = useState<PatientMatchResult[]>([]);
  const [matchingTier, setMatchingTier] = useState<"internal_id" | "external_id" | "name">("name");
  const [idColumn, setIdColumn] = useState("");
  const [mergeMode, setMergeMode] = useState<MergeMode>("append");
  const [resolvedMatches, setResolvedMatches] = useState<Record<string, { patient_id: number | null; action: string }>>({});

  // cursor_logs/043: if the user already picked an external-ID column in the
  // column-mapping step, the matching step should DEFAULT to matching by
  // external ID and REUSE that column — not start at "patient name" and re-ask
  // which column holds the ID. Fires when the chosen external-ID column changes;
  // a later manual change to `matchingTier` is preserved (this effect doesn't
  // re-run on it).
  useEffect(() => {
    if (externalIdColumn) {
      setMatchingTier("external_id");
      setIdColumn(externalIdColumn);
    }
  }, [externalIdColumn]);

  // Step 6: Confirm
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");

  // Processing state
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // ── Step 1: File Upload ──────────────────────────────────────────────────

  // Core parse function — called on initial upload and when header_row changes
  const parseFile = useCallback(async (file: File, hRow: number) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("environment_id", environmentId);
      formData.append("header_row", String(hRow));

      const resp = await api.post("/patients/bulk-upload/parse-tabular/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const result: ParseResult = resp.data;
      setParseResult(result);
      setHeaderRow(result.header_row ?? hRow);

      // Pre-fill the grouping column suggestion but leave grouping OFF by
      // default — "Each row = 1 patient" is Joan's preferred default. The
      // user can enable grouping explicitly if their spreadsheet really has
      // multiple rows per patient. (cursor_logs/036 §6)
      if (result.grouping_suggestion) {
        setGroupingColumn(result.grouping_suggestion.column);
      }

      // Check for saved templates
      try {
        const tmplResp = await api.post("/patients/bulk-upload/suggest-templates/", {
          columns: result.columns,
        });
        setSuggestedTemplates(tmplResp.data.templates || []);
      } catch {
        // Template suggestion is optional
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string; recoverable?: boolean; raw_preview?: (string | null)[][]; format_issues?: string[] } } };
      const errData = axiosErr.response?.data;

      // If the backend returned a recoverable error with raw preview,
      // show the preview so the user can pick a different header row
      if (errData?.recoverable && errData.raw_preview) {
        setParseResult({
          upload_id: "",
          file_name: file.name,
          row_count: 0,
          column_count: 0,
          columns: [],
          sample_rows: [],
          sheets: [],
          warnings: [],
          grouping_suggestion: null,
          header_row: hRow,
          raw_preview: errData.raw_preview,
          format_issues: errData.format_issues || [],
        });
        enqueueSnackbar(
          errData.error || t("csv.parse_failed", "Failed to parse file"),
          { variant: "warning" },
        );
      } else {
        enqueueSnackbar(
          errData?.error || t("csv.parse_failed", "Failed to parse file"),
          { variant: "error" },
        );
      }
    } finally {
      setLoading(false);
    }
  }, [environmentId, enqueueSnackbar, t]);

  // Drag-over state for the Step 1 drop zone (only enabled before a file has
  // been parsed — re-uploading goes through the "Change File" button).
  const [isDragging, setIsDragging] = useState(false);

  // Shared by the file input and the drop handler. Centralises the type/size
  // gates so they don't drift between code paths.
  const ingestFile = useCallback(async (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext || "")) {
      enqueueSnackbar(t("csv.invalid_type", "Please upload a .csv, .xlsx, or .xls file."), { variant: "error" });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      enqueueSnackbar(t("csv.too_large", "File exceeds the 50 MB limit."), { variant: "error" });
      return;
    }
    setLastUploadedFile(file);
    setHeaderRow(0);
    await parseFile(file, 0);
  }, [enqueueSnackbar, t, parseFile]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await ingestFile(file);
  }, [ingestFile]);

  // Auto-ingest the file the user dropped on the V2 form (if any) so the
  // wizard lands on the parsed-preview step instead of an empty uploader.
  // Guarded so it only fires once for a given file. Joan, 2026-05-27.
  const initialFileIngestedRef = useRef(false);
  useEffect(() => {
    if (initialFile && !initialFileIngestedRef.current) {
      initialFileIngestedRef.current = true;
      void ingestFile(initialFile);
    }
  }, [initialFile, ingestFile]);

  // The CSV/Excel tab is rendered inside AddPatientForm's bulk-mode container,
  // which has its own drop handler. We stop propagation so a drop on this
  // component doesn't double-fire as a Folder-tab drop.
  const handleDropZoneDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);

  const handleDropZoneDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDropZoneDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await ingestFile(file);
  }, [ingestFile]);

  // Re-parse when user changes header row
  const handleHeaderRowChange = useCallback(async (newRow: number) => {
    if (!lastUploadedFile || newRow === headerRow) return;
    setHeaderRow(newRow);
    // Reset schema inference since columns will change
    setSchemaInferred(false);
    setColumnDescriptions({});
    setPatientNameColumn("");
    await parseFile(lastUploadedFile, newRow);
  }, [lastUploadedFile, headerRow, parseFile]);

  // ── Step 4: Schema Inference ─────────────────────────────────────────────

  const runSchemaInference = useCallback(async () => {
    if (!parseResult) return;
    setLoading(true);
    try {
      const resp = await api.post("/patients/bulk-upload/infer-schema/", {
        upload_id: parseResult.upload_id,
        columns: parseResult.columns,
        sample_rows: parseResult.sample_rows,
      });

      const schema: SchemaResult = resp.data;
      setColumnDescriptions(schema.column_descriptions || {});
      // Pre-fill the suggested columns, but NEVER overwrite a choice the user
      // already made (re-running AI detection must not silently swap the name /
      // external-id column the user picked).
      const suggestedNameCol = schema.suggested_patient_name_column;
      if (suggestedNameCol) {
        setPatientNameColumn((prev) => prev || suggestedNameCol);
      }
      // External_id column is optional; only pre-fill when the LLM points to a
      // clear MRN/NHC/study-ID column, and only if the user hasn't chosen one.
      const suggestedExtCol = schema.suggested_external_id_column;
      if (suggestedExtCol) {
        setExternalIdColumn((prev) => prev || suggestedExtCol);
      }
      // Only pre-fill the suggested grouping COLUMN — don't auto-enable
      // grouping. "Each row = 1 patient" stays the default. (cursor_logs/036 §6)
      if (schema.suggested_grouping && !groupingEnabled) {
        setGroupingColumn(schema.suggested_grouping.column);
      }
      setSchemaInferred(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      enqueueSnackbar(
        axiosErr.response?.data?.error || t("csv.schema_inference_failed", "Schema inference failed"),
        { variant: "error" },
      );
      // Pre-populate with empty descriptions so user can fill manually
      const empty: Record<string, string> = {};
      parseResult.columns.forEach((c) => { empty[c] = ""; });
      setColumnDescriptions(empty);
      setSchemaInferred(true);
    } finally {
      setLoading(false);
    }
  }, [parseResult, enqueueSnackbar, groupingEnabled, t]);

  // Apply saved template
  const applyTemplate = useCallback((tmpl: TemplateMatch) => {
    setColumnDescriptions(tmpl.column_definitions);
    if (tmpl.grouping_config) {
      setGroupingEnabled(tmpl.grouping_config.enabled);
      setGroupingColumn(tmpl.grouping_config.column || "");
    }
    setSchemaInferred(true);
    enqueueSnackbar(t("csv.applied_template", 'Applied template "{name}"').replace("{name}", tmpl.name), { variant: "success" });
  }, [enqueueSnackbar, t]);

  // ── Step 5: Patient Matching (IMPORT mode) ───────────────────────────────

  const runPatientMatching = useCallback(async () => {
    if (!parseResult || mode !== "import") return;
    setLoading(true);
    try {
      // Determine patient identifiers based on patient name column or grouping column
      const idCol = idColumn || patientNameColumn || groupingColumn || parseResult.columns[0];
      const identifiers = parseResult.sample_rows.map((r) => r[idCol]).filter(Boolean);

      // For the full dataset, we'd need all unique identifiers
      // For now, use sample rows as preview
      const resp = await api.post("/patients/bulk-upload/match-patients/", {
        environment_id: environmentId,
        // Match across the whole batch's ticked envs (cursor_logs/049) so this
        // agrees with the cards/dedup path.
        environment_ids: environmentIds && environmentIds.length ? environmentIds : undefined,
        patient_identifiers: [...new Set(identifiers.map(String))],
        tier: matchingTier,
      });

      const results: PatientMatchResult[] = resp.data.results || [];
      setMatchResults(results);

      // Auto-populate resolved matches from results
      const autoResolved: Record<string, { patient_id: number | null; action: string }> = {};
      for (const mr of results) {
        if (mr.status === "matched" && mr.matches.length === 1) {
          autoResolved[mr.identifier] = { patient_id: mr.matches[0].id, action: "match" };
        } else if (mr.status === "not_found") {
          autoResolved[mr.identifier] = { patient_id: null, action: "create_new" };
        }
        // "ambiguous" left unresolved — user must choose
      }
      setResolvedMatches(autoResolved);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      enqueueSnackbar(
        axiosErr.response?.data?.error || t("csv.match_failed", "Patient matching failed"),
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  }, [parseResult, mode, environmentId, environmentIds, matchingTier, idColumn, patientNameColumn, groupingColumn, enqueueSnackbar, t]);

  // ── Step 6: Start Processing ─────────────────────────────────────────────

  /**
   * V2 cards-UI integration (cursor_logs/030 § 2026-05-22 editable wizard cards).
   * Posts to /bulk-upload/start-tabular/ with `review_only=true` to stage the
   * per-patient prompt text WITHOUT dispatching a Celery job. The V2 modal's
   * onReviewInCards callback materializes the staged patients as editable
   * cards in the V2 form; submit happens later via the unified bulk endpoint.
   */
  const startReviewOnly = useCallback(async () => {
    if (!parseResult || !onReviewInCards) return;
    setLoading(true);
    try {
      const resp = await api.post("/patients/bulk-upload/start-tabular/", {
        upload_id: parseResult.upload_id,
        environment_id: environmentId,
        // Multi-env targeting (cursor_logs/043).
        environment_ids: environmentIds && environmentIds.length ? environmentIds : undefined,
        mode,
        confirmed_descriptions: columnDescriptions,
        grouping: {
          enabled: groupingEnabled,
          column: groupingColumn,
        },
        patient_name_column: patientNameColumn,
        external_id_column: externalIdColumn || undefined,
        confirm_large_file: parseResult.exceeds_row_limit || parseResult.exceeds_col_limit || false,
        review_only: true,
      });

      const data = resp.data as {
        review_only: boolean;
        count: number;
        patients: Array<{
          row_index: number;
          name: string;
          external_id?: string;
          tabular_prompt_text: string;
        }>;
      };

      const staged: StagedTabularPatient[] = (data.patients || []).map((p) => ({
        rowIndex: p.row_index,
        name: p.name,
        externalId: p.external_id || "",
        tabularPromptText: p.tabular_prompt_text,
      }));

      onReviewInCards({
        uploadId: parseResult.upload_id,
        patients: staged,
      });
      // Don't enqueueSnackbar a success here — the V2 modal owns the UX
      // signal (it closes itself in response to onReviewInCards).
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      enqueueSnackbar(
        axiosErr.response?.data?.error ||
          t("csv.failed_review_stage", "Failed to stage review. Please retry."),
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  }, [
    parseResult,
    onReviewInCards,
    environmentId,
    environmentIds,
    mode,
    columnDescriptions,
    groupingEnabled,
    groupingColumn,
    patientNameColumn,
    externalIdColumn,
    enqueueSnackbar,
    t,
  ]);

  const startProcessing = useCallback(async () => {
    if (!parseResult) return;
    setLoading(true);
    try {
      const resp = await api.post("/patients/bulk-upload/start-tabular/", {
        upload_id: parseResult.upload_id,
        environment_id: environmentId,
        // Multi-env targeting (cursor_logs/043).
        environment_ids: environmentIds && environmentIds.length ? environmentIds : undefined,
        mode,
        confirmed_descriptions: columnDescriptions,
        grouping: {
          enabled: groupingEnabled,
          column: groupingColumn,
        },
        patient_name_column: patientNameColumn,
        external_id_column: externalIdColumn || undefined,
        matching: mode === "import" ? {
          tier: matchingTier,
          id_column: idColumn,
          resolved_matches: resolvedMatches,
          merge_mode: mergeMode,
        } : undefined,
        save_template: saveTemplate,
        template_name: templateName,
        confirm_large_file: parseResult.exceeds_row_limit || parseResult.exceeds_col_limit || false,
      });

      const jobIdNew = resp.data.job_id;
      setJobId(jobIdNew);
      setJobStatus(resp.data.status);
      setActiveStep(6); // Processing state

      // Start polling
      pollingRef.current = setInterval(async () => {
        try {
          const statusResp = await api.get(`/patients/bulk-upload/status/${jobIdNew}/`);
          const data = statusResp.data;
          setJobStatus(data.status);
          setProgress(data.progress_percent || 0);

          if (["completed", "partial", "failed", "cancelled"].includes(data.status)) {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setLoading(false);
            if (data.status === "completed") {
              enqueueSnackbar(
                t("csv.processed_success", "Successfully processed {count} patients from spreadsheet!").replace("{count}", String(data.processed_patients)),
                { variant: "success" },
              );
              onComplete?.();
            } else if (data.status === "partial") {
              enqueueSnackbar(
                t("csv.processed_partial", "Partially complete: {processed}/{total} patients.").replace("{processed}", String(data.processed_patients)).replace("{total}", String(data.total_patients)),
                { variant: "warning" },
              );
            } else if (data.status === "failed") {
              enqueueSnackbar(t("csv.processing_failed_msg", "Processing failed: {error}").replace("{error}", data.errors?.[0] || "Unknown error"), { variant: "error" });
            }
          }
        } catch {
          logger.error("Failed to poll tabular job status");
        }
      }, 3000);
    } catch (err: unknown) {
      setLoading(false);
      const axiosErr = err as { response?: { data?: { error?: string } } };
      enqueueSnackbar(
        axiosErr.response?.data?.error || t("csv.failed_start_processing", "Failed to start processing"),
        { variant: "error" },
      );
    }
  }, [
    parseResult, environmentId, environmentIds, mode, columnDescriptions, groupingEnabled,
    groupingColumn, patientNameColumn, externalIdColumn, matchingTier, idColumn,
    resolvedMatches, mergeMode, saveTemplate, templateName, enqueueSnackbar,
    onComplete, t,
  ]);

  // Auto-run schema inference when entering the Column Definitions step
  const inferenceTriggeredRef = useRef(false);
  useEffect(() => {
    if (activeStep === 3 && !schemaInferred && !loading && parseResult && !inferenceTriggeredRef.current) {
      inferenceTriggeredRef.current = true;
      runSchemaInference();
    }
    if (activeStep !== 3) {
      inferenceTriggeredRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // ── Navigation ───────────────────────────────────────────────────────────

  const handleNext = useCallback(async () => {
    if (activeStep === 3 && !schemaInferred) {
      // Re-run schema inference if not done yet
      await runSchemaInference();
      return;
    }
    if (activeStep === 4 && mode === "import" && matchResults.length === 0) {
      await runPatientMatching();
      return;
    }
    if (activeStep === 5) {
      await startProcessing();
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, 5));
  }, [activeStep, schemaInferred, mode, matchResults.length, runSchemaInference, runPatientMatching, startProcessing]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  // Can proceed to next step?
  const canProceed = (): boolean => {
    switch (activeStep) {
      case 0: return !!parseResult && !!parseResult.upload_id;
      case 1: return true;  // mode selected
      case 2: {
        // Grouping: if enabled, a column must be selected
        if (groupingEnabled && !groupingColumn) return false;
        return true;
      }
      case 3: {
        // Column definitions: need inference and patient name column
        if (!schemaInferred || !patientNameColumn) return false;
        // At least some descriptions should be filled
        const filledCount = Object.values(columnDescriptions).filter(Boolean).length;
        return filledCount > 0;
      }
      case 4: {
        // Patient resolution: for IMPORT mode, ensure ambiguous matches are resolved
        if (mode === "import" && matchResults.length > 0) {
          const unresolvedAmbiguous = matchResults.filter(
            (mr) => mr.status === "ambiguous" && !resolvedMatches[mr.identifier],
          );
          if (unresolvedAmbiguous.length > 0) return false;
        }
        return true;
      }
      case 5: {
        // Confirm: if saving template, name is required
        if (saveTemplate && !templateName.trim()) return false;
        return true;
      }
      default: return false;
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  // cursor_logs/049: in the dark V2 modal the inner cards were a washed-out
  // translucent WHITE (alpha(background.paper, 0.5)) that clashed with the dark
  // dialog. Dark glass — same surface the other Athena dialogs use — instead.
  const cardSx = {
    p: 2,
    border: `1px solid ${
      dark ? alpha(athenaColors.periwinkle, 0.22) : alpha(theme.palette.divider, 0.3)
    }`,
    borderRadius: 2,
    bgcolor: dark
      ? alpha(athenaColors.white, 0.05)
      : alpha(theme.palette.background.paper, 0.5),
  };

  // Force text / inputs / buttons / tables to the Athena dark-dialog palette
  // when hosted in the dark modal (cursor_logs/049). `!important` is needed to
  // beat the inline light-theme colours scattered through the wizard (e.g.
  // `color: text.primary` on the parse-preview <pre> blocks, `color="primary"`
  // labels) that would otherwise go dark-on-dark and vanish. Applied at the
  // wizard root so every step inherits it; a no-op in the light V1 mount.
  const darkRootSx = dark
    ? {
        color: athenaColors.white,
        "& .MuiTypography-root": { color: `${athenaColors.white} !important` },
        "& .MuiTypography-caption, & .MuiTypography-colorTextSecondary": {
          color: `${alpha(athenaColors.white, 0.6)} !important`,
        },
        "& .MuiFormControlLabel-label": {
          color: `${athenaColors.white} !important`,
        },
        "& .MuiTableCell-root": {
          color: alpha(athenaColors.white, 0.85),
          borderColor: alpha(athenaColors.periwinkle, 0.18),
        },
        "& pre": { color: `${alpha(athenaColors.white, 0.9)} !important` },
        // Inputs / selects
        "& .MuiInputBase-input, & .MuiSelect-select": {
          color: athenaColors.white,
        },
        "& .MuiInputLabel-root": { color: alpha(athenaColors.white, 0.7) },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: alpha(athenaColors.periwinkle, 0.35),
        },
        "& .MuiSvgIcon-root": { color: alpha(athenaColors.white, 0.7) },
        // Buttons → Athena dark-dialog styling (proper fill + hover) so the
        // footer Back/Next and the in-content buttons stop looking low-quality.
        "& .MuiButton-contained": {
          backgroundColor: athenaColors.periwinkle,
          color: athenaColors.white,
          boxShadow: "none",
          "& .MuiSvgIcon-root": { color: athenaColors.white },
          "&:hover": {
            backgroundColor: alpha(athenaColors.periwinkle, 0.85),
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            backgroundColor: alpha(athenaColors.periwinkle, 0.2),
            color: alpha(athenaColors.periwinkle, 0.5),
          },
        },
        "& .MuiButton-outlined": {
          color: alpha(athenaColors.white, 0.85),
          borderColor: alpha(athenaColors.periwinkle, 0.5),
          "& .MuiSvgIcon-root": { color: alpha(athenaColors.white, 0.85) },
          "&:hover": {
            borderColor: athenaColors.periwinkle,
            backgroundColor: alpha(athenaColors.white, 0.05),
          },
          "&.Mui-disabled": {
            color: alpha(athenaColors.white, 0.3),
            borderColor: alpha(athenaColors.periwinkle, 0.2),
          },
        },
      }
    : {};

  // The header-row picker table (step 1) colours its cells via INLINE `style`
  // (per-cell selected/skipped state), which class-based overrides can't reach
  // — so its text stayed dark-on-dark in the modal (cursor_logs/049 follow-up).
  // These dark-aware values mirror the (working) step-3 preview table.
  const hdrTbl = {
    text: dark ? alpha(athenaColors.white, 0.88) : theme.palette.text.primary,
    selected: dark ? athenaColors.lightPeriwinkle : theme.palette.primary.main,
    skipped: dark ? alpha(athenaColors.white, 0.3) : theme.palette.text.disabled,
    rownum: dark ? alpha(athenaColors.white, 0.55) : theme.palette.text.secondary,
    selBg: dark
      ? alpha(athenaColors.periwinkle, 0.22)
      : alpha(theme.palette.primary.main, 0.12),
    cellSelBg: dark
      ? alpha(athenaColors.periwinkle, 0.12)
      : alpha(theme.palette.primary.main, 0.06),
    skipBg: dark
      ? alpha(athenaColors.white, 0.04)
      : alpha(theme.palette.action.disabled, 0.04),
    border: dark
      ? alpha(athenaColors.periwinkle, 0.18)
      : alpha(theme.palette.divider, 0.2),
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Reset wizard to initial state for a new upload
  const handleStartOver = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setActiveStep(0);
    setParseResult(null);
    setHeaderRow(0);
    setLastUploadedFile(null);
    setMode("create");
    setGroupingEnabled(false);
    setGroupingColumn("");
    setColumnDescriptions({});
    setEditingColumn(null);
    setEditValue("");
    setPatientNameColumn("");
    setSuggestedTemplates([]);
    setSchemaInferred(false);
    setMatchResults([]);
    setMatchingTier("name");
    setIdColumn("");
    setMergeMode("append");
    setResolvedMatches({});
    setSaveTemplate(false);
    setTemplateName("");
    setJobId(null);
    setJobStatus(null);
    setProgress(0);
    setLoading(false);
  }, []);

  // Processing state (after step 6)
  if (activeStep === 6 && jobId) {
    const isTerminal = ["completed", "partial", "failed", "cancelled"].includes(jobStatus || "");
    return (
      <Box sx={{ ...cardSx, ...darkRootSx }}>
        <Typography variant="subtitle1" gutterBottom>
          {t("csv.processing_title", "Processing Spreadsheet Import")}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 10, borderRadius: 5, mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary">
          {jobStatus === "processing" ? t("csv.status_processing", "Processing... {progress}%").replace("{progress}", String(progress)) :
           jobStatus === "completed" ? t("csv.status_completed", "Completed!") :
           jobStatus === "partial" ? t("csv.status_partial", "Partially completed") :
           jobStatus === "failed" ? t("csv.status_failed", "Failed") :
           jobStatus === "queued" ? t("csv.status_queued", "Queued, waiting for active slot...") :
           `Status: ${jobStatus}`}
        </Typography>
        {isTerminal && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleStartOver}
            sx={{ mt: 1.5, textTransform: "none" }}
          >
            {t("csv.start_over", "Upload Another File")}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={darkRootSx}>
      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
        {getStepLabels(t).map((label) => (
          <Step key={label}>
            <StepLabel sx={{ "& .MuiStepLabel-label": { fontSize: "0.75rem" } }}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={cardSx}>

        {/* ── Step 1: Upload ──────────────────────────────────────────── */}
        {activeStep === 0 && (
          <Stack
            spacing={2}
            alignItems="center"
            onDragOver={handleDropZoneDragOver}
            onDragLeave={handleDropZoneDragLeave}
            onDrop={handleDropZoneDrop}
            sx={{
              position: "relative",
              width: "100%",
              p: 2,
              borderRadius: 2,
              border: isDragging
                ? `2px dashed ${theme.palette.primary.main}`
                : "2px dashed transparent",
              bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.04) : "transparent",
              transition: "border-color 120ms ease, background-color 120ms ease",
            }}
          >
            {isDragging && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" color="primary" fontWeight={600}>
                  {t("csv.drop_hint", "Drop .csv / .xlsx / .xls to upload")}
                </Typography>
              </Box>
            )}

            {!parseResult && (
              <Box
                component="pre"
                sx={{
                  m: 0, p: 1.5, fontSize: "0.7rem", lineHeight: 1.5,
                  fontFamily: "'JetBrains Mono', monospace",
                  bgcolor: alpha(theme.palette.common.black, 0.04),
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  borderRadius: 1, overflowX: "auto", whiteSpace: "pre",
                  color: theme.palette.text.secondary, width: "100%",
                }}
              >
                {CSV_ASCII}
              </Box>
            )}

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              accept=".csv,.xlsx,.xls"
            />

            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              {loading
                ? t("csv.parsing", "Parsing file...")
                : parseResult
                ? t("csv.change_file", "Change File")
                : t("csv.select_file", "Select CSV or Excel File or drop one here")}
            </Button>

            {!parseResult && (
              <Typography variant="caption" color="text.secondary">
                {t("csv.max_size", "Maximum file size: 50 MB")}
              </Typography>
            )}

            {parseResult && parseResult.upload_id && (
              <Alert severity="success" sx={{ width: "100%" }}>
                <strong>{parseResult.file_name}</strong>: {parseResult.row_count} rows,{" "}
                {parseResult.column_count} columns
                {parseResult.sheets.length > 1 && ` (${parseResult.sheets.length} sheets)`}
              </Alert>
            )}

            {parseResult?.warnings.map((w, i) => (
              <Alert key={i} severity="warning" sx={{ width: "100%" }}>
                {w}
              </Alert>
            ))}

            {/* ── Format issues guidance ───────────────────────────────── */}
            {parseResult && parseResult.format_issues && parseResult.format_issues.length > 0 && (
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{
                  width: "100%",
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("csv.format_issue_title", "Spreadsheet format issue detected")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {parseResult.format_issues.includes("merged_cells")
                    ? t(
                        "csv.format_issue_merged",
                        "This file contains merged cells in the first row(s). The first row may be a category/group header, not the actual column headers. Try selecting the correct header row below, or unmerge cells in your spreadsheet and re-upload.",
                      )
                    : t(
                        "csv.format_issue_multirow",
                        "The first row appears to contain category labels rather than column headers. Try selecting the correct header row below, or restructure your spreadsheet so column headers are on a single row.",
                      )}
                </Typography>

                {/* Good example */}
                <Typography variant="caption" fontWeight={700} color="success.dark" sx={{ display: "block", mb: 0.5 }}>
                  ✅ {t("csv.format_good_label", "Correct format: single header row, no merged cells:")}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0, mb: 1.5, p: 1, fontSize: "0.65rem", lineHeight: 1.4,
                    fontFamily: "'JetBrains Mono', monospace",
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                    borderRadius: 1, overflowX: "auto", whiteSpace: "pre",
                    color: theme.palette.text.primary,
                  }}
                >
                  {FORMAT_GOOD}
                </Box>

                {/* Bad example 1: merged cells */}
                <Typography variant="caption" fontWeight={700} color="error.dark" sx={{ display: "block", mb: 0.5 }}>
                  ❌ {t("csv.format_bad_merged_label", "Incorrect: merged category cells above headers:")}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0, mb: 1.5, p: 1, fontSize: "0.65rem", lineHeight: 1.4,
                    fontFamily: "'JetBrains Mono', monospace",
                    bgcolor: alpha(theme.palette.error.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    borderRadius: 1, overflowX: "auto", whiteSpace: "pre",
                    color: theme.palette.text.primary,
                  }}
                >
                  {FORMAT_BAD_MERGED}
                </Box>

                {/* Bad example 2: multi-row header */}
                <Typography variant="caption" fontWeight={700} color="error.dark" sx={{ display: "block", mb: 0.5 }}>
                  ❌ {t("csv.format_bad_multi_label", "Incorrect: extra row above the real headers:")}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0, p: 1, fontSize: "0.65rem", lineHeight: 1.4,
                    fontFamily: "'JetBrains Mono', monospace",
                    bgcolor: alpha(theme.palette.error.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    borderRadius: 1, overflowX: "auto", whiteSpace: "pre",
                    color: theme.palette.text.primary,
                  }}
                >
                  {FORMAT_BAD_MULTI}
                </Box>

                {/* Quick fix button: auto-select suggested header row */}
                {(() => {
                  const suggestedRow = parseResult.format_issues
                    .find((f) => f.startsWith("suggested_header_row:"));
                  if (suggestedRow) {
                    const rowNum = parseInt(suggestedRow.split(":")[1], 10);
                    return (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={() => handleHeaderRowChange(rowNum)}
                        disabled={loading}
                        sx={{ mt: 1, textTransform: "none", fontWeight: 600 }}
                      >
                        {t("csv.use_suggested_header", 'Use row {row} as header (recommended)').replace("{row}", String(rowNum + 1))}
                      </Button>
                    );
                  }
                  return null;
                })()}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  {t("csv.format_fix_hint", "Tip: You can click on a row in the preview below to select it as the header row. Alternatively, open the file in Excel, unmerge any merged cells, ensure headers are on a single row, then re-upload.")}
                </Typography>
              </Alert>
            )}

            {/* ── Raw preview + header row selector ────────────────────── */}
            {parseResult && parseResult.raw_preview && parseResult.raw_preview.length > 0 && (
              <Box sx={{ width: "100%" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("csv.header_row_title", "Select header row")}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  {t("csv.header_row_help", "Click a row to use it as column headers. Rows above it will be skipped.")}
                </Typography>

                <Box sx={{ maxHeight: 240, overflow: "auto", borderRadius: 1, border: `1px solid ${hdrTbl.border}` }}>
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      fontSize: "0.7rem",
                      borderCollapse: "collapse",
                      tableLayout: "auto",
                      "& td": {
                        border: `1px solid ${hdrTbl.border}`,
                        px: 0.75,
                        py: 0.4,
                        whiteSpace: "nowrap",
                        maxWidth: 140,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  >
                    <tbody>
                      {parseResult.raw_preview.map((row, rowIdx) => {
                        const isSelected = rowIdx === headerRow;
                        const isSkipped = rowIdx < headerRow;
                        return (
                          <tr
                            key={rowIdx}
                            onClick={() => handleHeaderRowChange(rowIdx)}
                            style={{ cursor: "pointer" }}
                          >
                            {/* Row number indicator */}
                            <td
                              style={{
                                fontWeight: 700,
                                textAlign: "center",
                                width: 44,
                                minWidth: 44,
                                color: isSelected
                                  ? hdrTbl.selected
                                  : isSkipped
                                  ? hdrTbl.skipped
                                  : hdrTbl.rownum,
                                background: isSelected ? hdrTbl.selBg : "transparent",
                              }}
                            >
                              {isSelected ? "▶ " : ""}{rowIdx + 1}
                            </td>
                            {row.slice(0, 15).map((cell, colIdx) => (
                              <td
                                key={colIdx}
                                style={{
                                  fontWeight: isSelected ? 700 : 400,
                                  color: isSkipped
                                    ? hdrTbl.skipped
                                    : isSelected
                                    ? hdrTbl.selected
                                    : hdrTbl.text,
                                  background: isSelected
                                    ? hdrTbl.cellSelBg
                                    : isSkipped
                                    ? hdrTbl.skipBg
                                    : "transparent",
                                  textDecoration: isSkipped ? "line-through" : "none",
                                }}
                              >
                                {cell ?? "-"}
                              </td>
                            ))}
                            {row.length > 15 && (
                              <td style={{ color: hdrTbl.skipped }}>
                                …+{row.length - 15}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  {t("csv.header_row_current", "Header row: {row}").replace("{row}", String(headerRow + 1))}
                  {headerRow > 0 && ` (${headerRow} ${headerRow === 1 ? t("csv.row_skipped", "row skipped") : t("csv.rows_skipped", "rows skipped")})`}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        {/* ── Step 2: Mode Selection ──────────────────────────────────── */}
        {activeStep === 1 && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">
              {t("csv.mode_title", "What would you like to do?")}
            </Typography>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as WizardMode)}>
              <FormControlLabel
                value="create"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t("csv.mode_create", "CREATE new patients")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("csv.mode_create_desc", "Each row (or group of rows) becomes a new patient in the platform.")}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="import"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t("csv.mode_import", "IMPORT data into existing patients")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("csv.mode_import_desc", "Match rows to patients that already exist and add new data.")}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Stack>
        )}

        {/* ── Step 3: Grouping Detection ──────────────────────────────── */}
        {activeStep === 2 && parseResult && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">
              {t("csv.grouping_title", "Row Structure")}
            </Typography>

            {parseResult.grouping_suggestion && (
              <Alert severity="info" icon={<InfoIcon />}>
                {parseResult.grouping_suggestion.reason}
              </Alert>
            )}

            <RadioGroup
              value={groupingEnabled ? "grouped" : "individual"}
              onChange={(e) => setGroupingEnabled(e.target.value === "grouped")}
            >
              <FormControlLabel
                value="individual"
                control={<Radio />}
                label={
                  <Typography variant="body2">
                    {t("csv.grouping_individual", "Each row = one patient")}
                  </Typography>
                }
              />
              <FormControlLabel
                value="grouped"
                control={<Radio />}
                label={
                  <Typography variant="body2">
                    {t("csv.grouping_grouped", "Group rows by a column (multiple events per patient)")}
                  </Typography>
                }
              />
            </RadioGroup>

            {groupingEnabled && (
              <FormControl fullWidth size="small">
                <InputLabel>{t("csv.grouping_column", "Group by column")}</InputLabel>
                <Select
                  value={groupingColumn}
                  onChange={(e) => setGroupingColumn(e.target.value)}
                  label={t("csv.grouping_column", "Group by column")}
                >
                  {parseResult.columns.map((col) => (
                    <MenuItem key={col} value={col}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Sample data preview */}
            <Box sx={{ maxHeight: 200, overflow: "auto" }}>
              <Typography variant="caption" fontWeight={600} gutterBottom>
                {t("csv.preview_title", "Data Preview")} ({parseResult.sample_rows.length} rows)
              </Typography>
              <Box
                component="table"
                sx={{
                  width: "100%", fontSize: "0.7rem", borderCollapse: "collapse",
                  "& td, & th": {
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    px: 0.5, py: 0.25, whiteSpace: "nowrap", maxWidth: 120,
                    overflow: "hidden", textOverflow: "ellipsis",
                  },
                  "& th": { fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) },
                }}
              >
                <thead>
                  <tr>
                    {parseResult.columns.slice(0, 10).map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                    {parseResult.columns.length > 10 && <th>...</th>}
                  </tr>
                </thead>
                <tbody>
                  {parseResult.sample_rows.slice(0, 5).map((row, i) => (
                    <tr key={i}>
                      {parseResult.columns.slice(0, 10).map((col) => (
                        <td key={col}>{row[col] == null ? "-" : String(row[col])}</td>
                      ))}
                      {parseResult.columns.length > 10 && <td>...</td>}
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          </Stack>
        )}

        {/* ── Step 4: Schema Inference + Editing ──────────────────────── */}
        {activeStep === 3 && parseResult && (
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle2">
                {t("csv.schema_title", "Column Definitions")}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={runSchemaInference}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} /> : <TableIcon />}
              >
                {loading
                  ? t("csv.inferring", "Analyzing...")
                  : schemaInferred
                  ? t("csv.rerun_inference", "Re-run AI Detection")
                  : t("csv.run_inference", "Auto-Detect with AI")}
              </Button>
            </Box>

            {/* Saved template suggestions */}
            {suggestedTemplates.length > 0 && !schemaInferred && (
              <Alert severity="info">
                <Typography variant="caption" gutterBottom>
                  {t("csv.template_suggestion", "We found saved templates matching your columns:")}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {suggestedTemplates.slice(0, 3).map((tmpl) => (
                    <Chip
                      key={tmpl.id}
                      label={`${tmpl.name} (${tmpl.overlap_percent}%)`}
                      size="small"
                      onClick={() => applyTemplate(tmpl)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary">
              {t("csv.schema_help", "These descriptions will guide the AI when processing each patient. Edit any incorrect descriptions.")}
            </Typography>

            {/* Patient name column selector */}
            <FormControl fullWidth size="small">
              <InputLabel>{t("csv.patient_name_col", "Patient Name / ID Column")}</InputLabel>
              <Select
                value={patientNameColumn}
                onChange={(e) => setPatientNameColumn(e.target.value)}
                label={t("csv.patient_name_col", "Patient Name / ID Column")}
              >
                {parseResult.columns.map((col) => (
                  <MenuItem key={col} value={col}>{col}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* External_id column selector (optional). When set, rows whose
                external_id matches an existing patient in the env get merged
                into that patient (silently if names agree, refused if they
                differ). See cursor_logs/036 §15. */}
            <FormControl fullWidth size="small">
              <InputLabel shrink id="csv-external-id-col-label">
                {t(
                  "csv.external_id_col",
                  "External ID Column (optional — for batch updates)",
                )}
              </InputLabel>
              <Select
                labelId="csv-external-id-col-label"
                value={externalIdColumn}
                onChange={(e) => setExternalIdColumn(e.target.value)}
                label={t(
                  "csv.external_id_col",
                  "External ID Column (optional — for batch updates)",
                )}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  <em>
                    {t(
                      "csv.external_id_none",
                      "None (rows always create new patients)",
                    )}
                  </em>
                </MenuItem>
                {parseResult.columns.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Descriptions progress indicator */}
            {schemaInferred && (() => {
              const filledCount = Object.values(columnDescriptions).filter(Boolean).length;
              const totalCount = parseResult.columns.length;
              const allFilled = filledCount === totalCount;
              return (
                <Typography variant="caption" sx={{ color: allFilled ? "success.main" : "warning.dark" }}>
                  {allFilled
                    ? t("csv.all_columns_described", "All {count} columns have descriptions.").replace("{count}", String(totalCount))
                    : t("csv.columns_described_count", "{filled}/{total} columns have descriptions. Columns without descriptions will use their header name.").replace("{filled}", String(filledCount)).replace("{total}", String(totalCount))
                  }
                </Typography>
              );
            })()}

            {/* Column descriptions list */}
            <Stack spacing={0.5} sx={{ maxHeight: 350, overflow: "auto" }}>
              {parseResult.columns.map((col) => (
                <Box
                  key={col}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    p: 1, borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                  }}
                >
                  <Chip
                    label={col}
                    size="small"
                    sx={{
                      fontFamily: "monospace", fontSize: "0.75rem",
                      minWidth: 80, fontWeight: 600,
                    }}
                  />
                  {editingColumn === col ? (
                    <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        fullWidth
                        autoFocus
                        multiline
                        maxRows={2}
                        sx={{ "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setColumnDescriptions((prev) => ({ ...prev, [col]: editValue }));
                          setEditingColumn(null);
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setEditingColumn(null)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          color: columnDescriptions[col]
                            ? theme.palette.text.primary
                            : theme.palette.text.disabled,
                          fontStyle: columnDescriptions[col] ? "normal" : "italic",
                          fontSize: "0.8rem",
                        }}
                      >
                        {columnDescriptions[col] || t("csv.no_description", "(no description)")}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingColumn(col);
                          setEditValue(columnDescriptions[col] || "");
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              ))}
            </Stack>
          </Stack>
        )}

        {/* ── Step 5: Patient Resolution ──────────────────────────────── */}
        {activeStep === 4 && parseResult && (
          <Stack spacing={2}>
            {mode === "create" ? (
              <>
                <Typography variant="subtitle2">
                  {t("csv.create_preview_title", "Patients to Create")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {groupingEnabled
                    ? t("csv.create_grouped_desc",
                        "Rows will be grouped by the selected column. Each group becomes one patient.")
                    : t("csv.create_individual_desc",
                        "Each row will create one patient.")}
                </Typography>
                <Alert severity="info">
                  <strong>{parseResult.row_count}</strong> rows →{" "}
                  <strong>
                    {groupingEnabled
                      ? `grouped by "${groupingColumn}"`
                      : `${parseResult.row_count} patients`}
                  </strong>
                </Alert>
              </>
            ) : (
              <>
                <Typography variant="subtitle2">
                  {t("csv.import_title", "Match Patients")}
                </Typography>

                {/* Matching tier */}
                <FormControl fullWidth size="small">
                  <InputLabel>{t("csv.match_by", "Match patients by")}</InputLabel>
                  <Select
                    value={matchingTier}
                    onChange={(e) => setMatchingTier(e.target.value as typeof matchingTier)}
                    label={t("csv.match_by", "Match patients by")}
                  >
                    <MenuItem value="internal_id">{t("csv.internal_id", "Internal Platform ID")}</MenuItem>
                    <MenuItem value="external_id">{t("csv.external_id", "External ID (MRN)")}</MenuItem>
                    <MenuItem value="name">{t("csv.patient_name_option", "Patient Name")}</MenuItem>
                  </Select>
                </FormControl>

                {/* cursor_logs/043: when matching by external ID and a column
                    was already chosen in the mapping step, reuse it instead of
                    re-asking — just show which column is used. Otherwise (or for
                    internal-ID matching) ask for the column. */}
                {matchingTier === "external_id" && externalIdColumn ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("csv.matching_on_column", "Matching on column")}: <strong>{externalIdColumn}</strong>
                  </Typography>
                ) : (
                  matchingTier !== "name" && (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t("csv.id_column", "ID Column in File")}</InputLabel>
                      <Select
                        value={idColumn}
                        onChange={(e) => setIdColumn(e.target.value)}
                        label={t("csv.id_column", "ID Column in File")}
                      >
                        {parseResult.columns.map((col) => (
                          <MenuItem key={col} value={col}>{col}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )
                )}

                {/* Merge mode — only meaningful in the V1 standalone direct-submit
                    path (startProcessing sends `matching.merge_mode`). In V2 the
                    finish action is "Edit patient cards before creating" →
                    startReviewOnly → cards → bulk-upload/start, which does its own
                    per-external-id auto-merge and never receives merge_mode, so
                    these radios would be inert/misleading (REPLACE not honoured).
                    Hide them whenever the cards-review path is available
                    (cursor_logs/049 § merge-mode-dead-in-V2, decision (a)). */}
                {!onReviewInCards && (
                  <>
                    <Divider />
                    <Typography variant="caption" fontWeight={600}>
                      {t("csv.merge_mode_title", "Merge Mode")}
                    </Typography>
                    <RadioGroup value={mergeMode} onChange={(e) => setMergeMode(e.target.value as MergeMode)}>
                      <FormControlLabel
                        value="append"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            {t("csv.merge_append", "APPEND: Add new rows only (safe)")}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="upsert"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            {t("csv.merge_upsert", "UPSERT: Add new + update matching rows")}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="replace"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2" color="error">
                            {t("csv.merge_replace", "REPLACE: Delete existing, insert new (destructive)")}
                          </Typography>
                        }
                      />
                    </RadioGroup>

                    {mergeMode === "replace" && (
                      <Alert severity="error">
                        {t("csv.replace_warning",
                          "REPLACE mode will delete all existing data in the target domain tables for matched patients. This cannot be undone.")}
                      </Alert>
                    )}
                  </>
                )}

                {/* Match results */}
                {matchResults.length > 0 && (
                  <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                    {matchResults.map((mr) => (
                      <Box
                        key={mr.identifier}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1,
                          p: 0.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                        }}
                      >
                        <Chip
                          label={mr.identifier}
                          size="small"
                          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                        />
                        <Chip
                          label={mr.status}
                          size="small"
                          color={mr.status === "matched" ? "success" : mr.status === "ambiguous" ? "warning" : "error"}
                          variant="outlined"
                        />
                        {mr.matches.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            → {mr.matches[0].name} (ID: {mr.matches[0].id})
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {matchResults.length === 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={runPatientMatching}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={14} /> : undefined}
                  >
                    {loading ? t("csv.matching", "Matching...") : t("csv.run_matching", "Preview Matches")}
                  </Button>
                )}
              </>
            )}
          </Stack>
        )}

        {/* ── Step 6: Confirm & Start ─────────────────────────────────── */}
        {activeStep === 5 && parseResult && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">
              {t("csv.confirm_title", "Confirm & Start Processing")}
            </Typography>

            {/* Summary */}
            <Box sx={{
              p: 1.5, borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              bgcolor: alpha(theme.palette.info.main, 0.04),
            }}>
              <Typography variant="body2">
                <strong>{t("csv.summary_mode", "Mode")}:</strong>{" "}
                {mode === "create" ? t("csv.summary_create", "CREATE new patients") : t("csv.summary_import", "IMPORT into existing")}
              </Typography>
              <Typography variant="body2">
                <strong>{t("csv.summary_rows", "Total rows")}:</strong> {parseResult.row_count}
              </Typography>
              <Typography variant="body2">
                <strong>{t("csv.summary_columns", "Columns")}:</strong> {parseResult.column_count}
              </Typography>
              <Typography variant="body2">
                <strong>{t("csv.summary_grouping", "Grouping")}:</strong>{" "}
                {groupingEnabled ? t("csv.grouping_by_col", 'By "{column}"').replace("{column}", groupingColumn || "") : t("csv.each_row_patient", "Each row = 1 patient")}
              </Typography>
              <Typography variant="body2">
                <strong>{t("csv.summary_patient_col", "Patient column")}:</strong> {patientNameColumn}
              </Typography>
              <Typography variant="body2">
                <strong>{t("csv.summary_descriptions", "Described columns")}:</strong>{" "}
                {Object.values(columnDescriptions).filter(Boolean).length}/{parseResult.columns.length}
              </Typography>
            </Box>

            {/* Cost estimate */}
            {(() => {
              const estimatedCost = (parseResult.row_count * 950 * 0.00000015) + (parseResult.row_count * 500 * 0.000002);
              const isHighCost = estimatedCost > 5;
              return (
                <Box sx={{
                  p: 1.5, borderRadius: 1.5,
                  border: `1px solid ${alpha(isHighCost ? theme.palette.warning.main : theme.palette.divider, 0.3)}`,
                  bgcolor: alpha(isHighCost ? theme.palette.warning.main : theme.palette.divider, 0.04),
                }}>
                  <Typography variant="caption" fontWeight={600} color={isHighCost ? "warning.main" : "text.secondary"}>
                    {t("csv.cost_title", "Estimated Cost")}
                  </Typography>
                  <Typography variant="body2">
                    ~{(parseResult.row_count * 950 / 1000).toFixed(0)}K tokens total
                    {" ≈ €"}{estimatedCost.toFixed(3)}
                  </Typography>
                  {isHighCost && (
                    <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                      {t("csv.high_cost_warning", "This import will cost more than €5. Please verify this is the correct file and that you have sufficient quota.")}
                    </Typography>
                  )}
                </Box>
              );
            })()}

            {/* Save template */}
            <FormControlLabel
              control={
                <input
                  type="checkbox"
                  checked={saveTemplate}
                  onChange={(e) => setSaveTemplate(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
              }
              label={
                <Typography variant="body2">
                  {t("csv.save_template", "Save column definitions as a template for future uploads")}
                </Typography>
              }
            />

            {saveTemplate && (
              <TextField
                size="small"
                label={t("csv.template_name", "Template Name")}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t("csv.template_placeholder", "e.g., Breast cancer cohort export")}
              />
            )}
          </Stack>
        )}
      </Box>

      {/* Navigation buttons — sticky footer so they stay visible without
          scrolling. The modal that hosts this wizard no longer renders its own
          footer; this row IS the footer. */}
      <Box sx={{
        display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap",
        position: "sticky", bottom: 0, zIndex: 2, mt: 1, pt: 1.5, pb: 0.5,
        // cursor_logs/049: the dialog paper is a deepNavy→royalBlue gradient
        // (athenaDarkPaperSx). Any solid fill here (even royalBlue@0.96) reads as
        // a mismatched band against the gradient at the footer's exact y-position
        // (Joan, 2026-06-11). So in dark mode keep the fill fully TRANSPARENT —
        // the gradient shows through seamlessly — and rely on a frosted blur to
        // soften any long-form content that scrolls behind the sticky row.
        borderTop: `1px solid ${
          dark ? alpha(athenaColors.periwinkle, 0.2) : alpha(theme.palette.divider, 0.3)
        }`,
        bgcolor: dark ? "transparent" : "background.paper",
        backdropFilter: dark ? "blur(6px)" : undefined,
      }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
          sx={{ textTransform: "none" }}
        >
          {t("csv.back", "Back")}
        </Button>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {/* V2 cards-UI (cursor_logs/036 §6b): when the wizard is mounted inside
              the V2 modal (`onReviewInCards` supplied) the direct-submit path is
              intentionally REMOVED at the final step — every spreadsheet import
              must land in editable cards for review first (Joan, 2026-06-08). So
              at step 5 with `onReviewInCards` the ONLY action is "Review & edit in
              cards", promoted to the primary `variant="contained"` button. V1's
              standalone mount (no `onReviewInCards`) keeps its single
              "Start Processing" direct-submit button below, untouched. */}
          {activeStep === 5 && onReviewInCards && (
            <Button
              variant="contained"
              onClick={startReviewOnly}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              {t(
                "csv.edit_cards_before_create",
                "Edit patient cards before creating",
              )}
            </Button>
          )}

          {/* The Next / direct-submit button. Hidden at the final step ONLY when
              `onReviewInCards` is supplied (V2 review-only flow above takes over);
              still rendered for steps 0-4 (as "Next"/"Analyze") and at step 5 in
              the V1 standalone mount (as "Start Processing"). */}
          {!(activeStep === 5 && onReviewInCards) && (
            <Button
              variant="contained"
              endIcon={activeStep === 5 ? <StartIcon /> : <NextIcon />}
              onClick={handleNext}
              disabled={!canProceed() || loading}
              sx={{ textTransform: "none" }}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              {activeStep === 5
                ? t("csv.start_processing", "Start Processing")
                : activeStep === 3 && !schemaInferred
                ? t("csv.analyze", "Analyze with AI")
                : t("csv.next", "Next")}
            </Button>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

export default TabularWizard;
